import * as THREE from 'three';
import Experience from '../../Experience.js';

export default class ParticleImageBright {
    constructor(imageSource, distanceFromCamera = 10, particleDensity = 3, initAlpha = 1.0) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.time = this.experience.time;
        this.camera = this.experience.camera;
        this.renderer = this.experience.renderer;
        this.resources = this.experience.resources;

        // パーティクルシステム
        this.particleSystem = null;
        this.particles = [];
        this.originalPositions = [];
        this.material = null;

        this.distanceFromCamera = distanceFromCamera;
        this.particleDensity = particleDensity; // 1=最高密度, 2=1/2密度, 3=1/3密度...
        this.sparkleSpeed = 1.0; // 煌めきの速度
        this.sparkleIntensity = 1.0; // 煌めきの強度 (0.0～2.0)
        this.maxWidth = 400; // 最大幅
        this.maxHeight = 400; // 最大高さ
        this.initAlpha = initAlpha;

        // 煌めき範囲の直接制御（ParticleTextBrightと同じ機能を追加）
        this.scaleMin = 0.6;      // サイズの最小値
        this.scaleMax = 1.4;      // サイズの最大値
        this.brightnessMin = 0.5; // 明度の最小値
        this.brightnessMax = 1.0; // 明度の最大値

        // 画像設定
        this.currentImageSource = this.resources.items[imageSource]
        this.init();

        // アニメーションループに登録
        this.time.on('tick', () => {
            this.update();
        });
    }

    init() {
        this.createImageParticles(this.currentImageSource);
    }

    // カメラの前方位置とオリエンテーションを計算
    getCameraFrontTransform() {
        const cameraPosition = this.camera.instance.position.clone();
        const cameraDirection = new THREE.Vector3();
        this.camera.instance.getWorldDirection(cameraDirection);
        const targetPosition = cameraPosition.clone().add(
            cameraDirection.multiplyScalar(this.distanceFromCamera)
        );
        const cameraQuaternion = new THREE.Quaternion();
        this.camera.instance.getWorldQuaternion(cameraQuaternion);

        return {
            position: targetPosition,
            quaternion: cameraQuaternion,
            direction: cameraDirection.normalize()
        };
    }

    // 画像を読み込んでパーティクル化 (非同期化した、alpha値を制御するために、他のクラスからmaterialを参照したかったので)
    // async createImageParticles(imageSource) {
    createImageParticles(imageSource) {
        try {
            // 既存のパーティクルシステムを削除
            if (this.particleSystem) {
                this.scene.remove(this.particleSystem);
                this.particleSystem.geometry.dispose();
                this.particleSystem.material.dispose();
            }

            // 1. 画像を読み込み
            const texture = imageSource;
            const image = texture.image;

            //MEMO: 同期処理の時は、('/image/bonfire_icon.png') のようにインスタン化時にpath を渡して実行していた
            // const image = await this.loadImage(imageSource);

            // 2. キャンバスに画像を描画
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // アスペクト比を保持してリサイズ
            const { width, height } = this.calculateSize(image.width, image.height);
            canvas.width = width;
            canvas.height = height;

            // 画像をキャンバスに描画
            ctx.drawImage(image, 0, 0, width, height);

            // 3. ピクセルデータ取得
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // 4. カメラの前方位置とオリエンテーションを取得
            const cameraTransform = this.getCameraFrontTransform();

            // 5. パーティクル生成
            const positions = [];
            const colors = [];
            const scales = [];
            const randomOffsets = []; // 各パーティクルのランダムタイミング
            const originalPos = [];

            // パーティクル密度を設定（数値が大きいほど粗くなる）
            const step = Math.max(1, Math.floor(this.particleDensity));

            for (let y = 0; y < canvas.height; y += step) {
                for (let x = 0; x < canvas.width; x += step) {
                    const pixelIndex = (y * canvas.width + x) * 4;

                    // RGB値とアルファ値を取得
                    const r = data[pixelIndex] / 255;
                    const g = data[pixelIndex + 1] / 255;
                    const b = data[pixelIndex + 2] / 255;
                    const alpha = data[pixelIndex + 3];

                    // 透明でないピクセルのみパーティクル化
                    if (alpha > 50) {
                        // ローカル座標でのパーティクル位置
                        const localX = (x - canvas.width / 2) * 0.03;
                        const localY = (canvas.height / 2 - y) * 0.03;
                        const localZ = (Math.random() - 0.5) * 0.5;

                        // ローカル座標をベクトルに変換
                        const localPosition = new THREE.Vector3(localX, localY, localZ);

                        // カメラの回転を適用してワールド座標に変換
                        localPosition.applyQuaternion(cameraTransform.quaternion);

                        // カメラの前方位置に移動
                        const worldPosition = localPosition.add(cameraTransform.position);

                        positions.push(worldPosition.x, worldPosition.y, worldPosition.z);
                        originalPos.push(worldPosition.x, worldPosition.y, worldPosition.z);

                        // 画像の実際の色をそのまま使用
                        colors.push(r, g, b);

                        // 明度に基づいてスケールを調整 + ランダム要素を追加
                        const brightness = (r + g + b) / 3;
                        const baseScale = 0.3 + brightness * 0.7;
                        const randomScale = baseScale * (0.7 + Math.random() * 0.6); // ランダムなスケール調整
                        scales.push(randomScale);

                        // 各パーティクルに独立したランダムタイミングオフセットを追加
                        randomOffsets.push(Math.random() * Math.PI * 2);
                    }
                }
            }

            // パーティクルシステム作成
            this.createParticleSystem(positions, colors, scales, randomOffsets);
            this.originalPositions = originalPos;

            console.log(`Created ${positions.length / 3} particles for image (density: 1/${this.particleDensity})`);

            // 10秒後に自動削除（必要に応じて調整）
            setTimeout(() => {
                this.destroy();
            }, 10000);

        } catch (error) {
            console.error('画像の読み込みでエラーが発生しました:', error);
        }
    }

    // 画像を読み込むPromise関数
    loadImage(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous'; // CORS対応
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`画像の読み込みに失敗: ${src}`));
            img.src = src;
        });
    }

    // アスペクト比を保持してサイズを計算
    calculateSize(originalWidth, originalHeight) {
        const aspectRatio = originalWidth / originalHeight;

        let width = originalWidth;
        let height = originalHeight;

        // 最大サイズを超える場合はリサイズ
        if (width > this.maxWidth) {
            width = this.maxWidth;
            height = width / aspectRatio;
        }
        if (height > this.maxHeight) {
            height = this.maxHeight;
            width = height * aspectRatio;
        }

        return { width: Math.floor(width), height: Math.floor(height) };
    }

    // パーティクルシステムを作成
    createParticleSystem(positions, colors, scales, randomOffsets) {
        // ジオメトリ作成
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setAttribute('scale', new THREE.Float32BufferAttribute(scales, 1));
        geometry.setAttribute('randomOffset', new THREE.Float32BufferAttribute(randomOffsets, 1));

        // シェーダーマテリアル（煌めき効果付き - 輝き制御機能を追加）
        this.material = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                alpha: { value: this.initAlpha },
                sparkleIntensity: { value: this.sparkleIntensity },
                scaleMin: { value: this.scaleMin },
                scaleMax: { value: this.scaleMax },
                brightnessMin: { value: this.brightnessMin },
                brightnessMax: { value: this.brightnessMax }
            },
            vertexShader: `
                attribute float scale;
                attribute float randomOffset;
                varying vec3 vColor;
                varying float vScale;
                varying float vRandomOffset;
                uniform float time;
                uniform float sparkleIntensity;
                uniform float scaleMin;
                uniform float scaleMax;

                void main() {
                    vColor = color;
                    vScale = scale;
                    vRandomOffset = randomOffset;

                    vec3 pos = position;

                    // 微細な浮遊効果（煌めきに合わせて軽微に）
                    pos.y += sin(time * 0.8 + randomOffset + pos.x * 0.05) * 0.1;
                    pos.x += cos(time * 0.6 + randomOffset + pos.y * 0.05) * 0.08;

                    // サイズ変動を直接制御（ParticleTextBrightと同じロジック）
                    float range = scaleMax - scaleMin;
                    float twinkleScale = scaleMin + (sin(time * 3.0 + randomOffset) * 0.5 + 0.5) * range;
                    float flashEffect = scaleMin + (sin(time * 8.0 + randomOffset * 2.0) * 0.5 + 0.5) * range * 0.2;

                    // 最終的なスケールは2つの効果を組み合わせ
                    float finalScale = (twinkleScale + flashEffect) * 0.5;

                    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                    gl_PointSize = scale * finalScale * 0.6 * (200.0 / -mvPosition.z);
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                varying vec3 vColor;
                varying float vScale;
                varying float vRandomOffset;
                uniform float time;
                uniform float alpha;
                uniform float sparkleIntensity;
                uniform float brightnessMin;
                uniform float brightnessMax;

                void main() {
                    vec2 center = vec2(0.5);
                    float dist = distance(gl_PointCoord, center);

                    if (dist > 0.5) discard;

                    float intensity = 1.0 - smoothstep(0.0, 0.5, dist);
                    intensity = pow(intensity, 1.2);

                    // 明度変動を直接制御（ParticleTextBrightと同じロジック）
                    float brightnessRange = brightnessMax - brightnessMin;
                    float twinkle1 = brightnessMin + (sin(time * 4.0 + vRandomOffset) * 0.5 + 0.5) * brightnessRange;
                    float twinkle2 = brightnessMin + (sin(time * 6.0 + vRandomOffset * 1.5) * 0.5 + 0.5) * brightnessRange * 0.3;
                    float flash = brightnessMin + (sin(time * 12.0 + vRandomOffset * 3.0) * 0.5 + 0.5) * brightnessRange * 0.2;

                    // 複数の煌めきパターンを組み合わせ
                    float sparkle = (twinkle1 + twinkle2 + flash) / 3.0;

                    // 星のような輝きパターン
                    float starPattern = max(
                        smoothstep(0.45, 0.5, abs(gl_PointCoord.x - 0.5)) * smoothstep(0.1, 0.0, abs(gl_PointCoord.y - 0.5)),
                        smoothstep(0.45, 0.5, abs(gl_PointCoord.y - 0.5)) * smoothstep(0.1, 0.0, abs(gl_PointCoord.x - 0.5))
                    );
                    
                    intensity += starPattern * sparkle * 0.8;

                    // 色の強化（より鮮やかに）
                    vec3 enhancedColor = vColor;
                    
                    // 明度を制御された範囲で調整
                    enhancedColor = enhancedColor * sparkle + vec3(0.2 * (brightnessMax - 0.5));
                    
                    // 彩度の強化
                    vec3 luminance = vec3(0.299, 0.587, 0.114);
                    float lum = dot(enhancedColor, luminance);
                    enhancedColor = mix(vec3(lum), enhancedColor, 1.0 + 0.3 * sparkleIntensity);
                    
                    // 最終的な色の計算
                    vec3 finalColor = enhancedColor * (0.7 + 0.3 * vScale) * sparkle;
                    
                    // 輝きの中心部をより明るく
                    float centerGlow = 1.0 - smoothstep(0.0, 0.2, dist);
                    finalColor += vec3(0.3 * brightnessMax) * centerGlow * sparkle;

                    gl_FragColor = vec4(finalColor, intensity * 0.9 * sparkle * alpha);
                }
            `,
            transparent: true,
            vertexColors: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });

        // パーティクルシステム作成
        this.particleSystem = new THREE.Points(geometry, this.material);
        this.scene.add(this.particleSystem);
    }

    update() {
        if (!this.particleSystem) return;

        // シェーダーのuniformを更新（新しい制御パラメータを追加）
        this.particleSystem.material.uniforms.time.value = this.time.elapsed * 0.001 * this.sparkleSpeed;
        this.particleSystem.material.uniforms.sparkleIntensity.value = this.sparkleIntensity;
        this.particleSystem.material.uniforms.scaleMin.value = this.scaleMin;
        this.particleSystem.material.uniforms.scaleMax.value = this.scaleMax;
        this.particleSystem.material.uniforms.brightnessMin.value = this.brightnessMin;
        this.particleSystem.material.uniforms.brightnessMax.value = this.brightnessMax;
    }

    // サイズ変動範囲を直接設定（min: 最小倍率, max: 最大倍率）
    setScaleRange(min, max) {
        this.scaleMin = Math.max(0.1, min);
        this.scaleMax = Math.max(this.scaleMin + 0.1, max);
        
        if (this.particleSystem) {
            this.particleSystem.material.uniforms.scaleMin.value = this.scaleMin;
            this.particleSystem.material.uniforms.scaleMax.value = this.scaleMax;
        }
    }

    // 明度変動範囲を直接設定（min: 最小明度, max: 最大明度）
    setBrightnessRange(min, max) {
        this.brightnessMin = Math.max(0.0, Math.min(1.0, min));
        this.brightnessMax = Math.max(this.brightnessMin + 0.1, Math.min(1.0, max));
        
        if (this.particleSystem) {
            this.particleSystem.material.uniforms.brightnessMin.value = this.brightnessMin;
            this.particleSystem.material.uniforms.brightnessMax.value = this.brightnessMax;
        }
    }

    // 常に明るく、サイズも安定した設定にするプリセット
    setBrightStableMode() {
        this.setScaleRange(1.2, 1.5);     // サイズは1.2～1.5倍で変動
        this.setBrightnessRange(0.8, 1.0); // 明度は0.8～1.0で常に明るく
        this.setSparkleSpeed(0.8);          // ゆったりとした煌めき
    }

    // 控えめな煌めきプリセット
    setSubtleMode() {
        this.setScaleRange(0.9, 1.1);     // サイズは0.9～1.1倍で控えめ
        this.setBrightnessRange(0.6, 0.9); // 明度は0.6～0.9
        this.setSparkleSpeed(0.5);          // 遅い煌めき
    }

    // 激しい煌めきプリセット
    setIntenseMode() {
        this.setScaleRange(0.3, 2.0);     // サイズは0.3～2.0倍で激しく変動
        this.setBrightnessRange(0.1, 1.0); // 明度も大きく変動
        this.setSparkleSpeed(2.0);          // 高速煌めき
    }

    // 煌めき効果の強度を設定
    setSparkleIntensity(intensity) {
        this.sparkleIntensity = Math.max(0, Math.min(2, intensity));
        if (this.particleSystem && this.particleSystem.material.uniforms.sparkleIntensity) {
            this.particleSystem.material.uniforms.sparkleIntensity.value = this.sparkleIntensity;
        }
    }

    // 煌めきの速度を設定
    setSparkleSpeed(speed) {
        this.sparkleSpeed = Math.max(0.1, Math.min(5, speed));
    }

    // パーティクル密度を設定（1=最高密度, 2=1/2密度, 3=1/3密度...）
    setParticleDensity(density) {
        this.particleDensity = Math.max(1, density);
        // 現在の画像を再描画
        if (this.currentImageSource) {
            this.createImageParticles(this.currentImageSource);
        }
    }

    // カメラ位置が変わった時に画像を再配置
    updateToCurrentCameraPosition() {
        if (this.currentImageSource) {
            this.createImageParticles(this.currentImageSource);
        }
    }

    // カメラからの距離を変更
    setDistanceFromCamera(newDistance) {
        this.distanceFromCamera = newDistance;
        this.updateToCurrentCameraPosition();
    }

    // 画像変更メソッド
    changeImage(newImageSource) {
        this.currentImageSource = newImageSource;
        this.createImageParticles(newImageSource);
    }

    // 最大サイズを設定
    setMaxSize(width, height) {
        this.maxWidth = width;
        this.maxHeight = height;
    }

    // パーティクルシステムを削除
    destroy() {
        if (this.particleSystem) {
            this.scene.remove(this.particleSystem);
            this.particleSystem.geometry.dispose();
            this.particleSystem.material.dispose();
        }
    }
}